<script>
import FullCalendar from '@fullcalendar/vue3'
import resourceTimelinePlugin from '@fullcalendar/vue3-scheduler/resource-timeline';
import interactionPlugin from '@fullcalendar/vue3/interaction';
import { get, put, post, destroy } from '@rails/request.js';
import ModalDialogMixin from '../mixins/modal-dialog.vue'
import TimetableMixin from '../mixins/timetable.vue'

import luxonFormatPlugin from '@fullcalendar/format-luxon3'
import themePlugin from '@fullcalendar/vue3/themes/forma'
import '@fullcalendar/vue3/skeleton.css'
import '@fullcalendar/vue3/themes/forma/theme.css'
import '@fullcalendar/vue3/themes/forma/palettes/blue.css'

export default {
  components: {
    FullCalendar
  },
  mixins: [TimetableMixin, ModalDialogMixin],
  props: {
    eventId: String,
    volunteersUrl: String,
    shiftsUrl: String,
    attendancesUrl: String,
    shiftPositionsUrl: String,
    eventStartDate: String,
    eventEndDate: String,
  },
  data() {
    return {
      calendarOptions: {
        plugins: [luxonFormatPlugin, interactionPlugin, resourceTimelinePlugin, themePlugin],
        schedulerLicenseKey: 'AGPL-My-Frontend-And-Backend-Are-Open-Source',
        initialView: 'resourceTimeline',
        height: "auto",
        nowIndicator: true,
        locale: document.documentElement.lang,
        timeZone: this.timeZone(),
        slotDuration: '00:15:00',
        slotHeaderInterval: '00:30',
        titleFormat: 'ccc, DDD',
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
        },
        slotHeaderFormat: {
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: false,
        },
        headerToolbar: {
          left: 'title',
          right: 'toggleSlotHeaderInterval prev,next'
        },
        buttons: {
          toggleSlotHeaderInterval: {
            text: this.$i18n.t("timetable.buttons.toggle_slot_header_interval"),
            click: this.toggleSlotHeaderInterval,
            iconClass: 'bi bi-arrows-collapse-vertical',
            display: 'icon',
          }
        },
        validRange: {
          start: this.eventStartDate,
          end: this.eventEndDate
        },
        events: [{
          start: this.eventStartDate,
          end: this.eventEndDate,
          editable: false,
          display: 'inverse-background',
          color: 'grey'
        }],
        eventSources: [
          {
            id: "shifts",
            url: this.shiftsUrl,
          },
          {
            id: "attendances",
            editable: false,
            display: 'background',
            color: '#56bf69',
            url: this.attendancesUrl,
          }
        ],
        eventDrop: this.onDropOrResize,
        eventResize: this.onDropOrResize,
        dragRevertDuration: 0,
        selectable: true,
        select: this.onSelection,
        eventLongPressDelay: 300,
        eventClick: this.onClick,
        resources: this.volunteersUrl,
        resourceGroupField: "divisionName",
        resourceOrder: ['divisionName', 'title'],
        resourceColumns: [{
          field: 'title',
          headerContent: this.$i18n.t("timetable.volunteers.headers.volunteer"),
        }]
      },
      attendanceView: false,
      attendanceCreateOnSelection: null,
    }
  },
  channels: {
    EventsChannel: {
      received(data) {
        switch (data.event) {
          case "shifts_changed":
            for (const volunteerId of data.volunteer_ids) {
              this.refreshShiftsOfVolunteer(volunteerId)
            }
            break;
          case "shift_deleted":
            this.getEventByIdAndSource(data.shift_id, 'shifts')?.remove()
            break;
          case "attendances_changed":
            this.$refs.calendar.getApi().getEventSourceById("attendances").refetch()
            break;
          case "volunteers_changed":
            this.$refs.calendar.getApi().refetchResources()
            break;
        }
      }
    },
  },
  mounted() {
    this.$cable.subscribe({
      channel: "EventsChannel",
      eventId: this.eventId,
    });
  },
  methods: {
    activateAddAttendanceView: async function() {
      this.attendanceView = true
      this.attendanceCreateOnSelection = true

      document.querySelector("#dashboard-volunteers-btn-shifts").classList.remove("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-shifts").classList.add("btn-outline-secondary")
      document.querySelector("#dashboard-volunteers-btn-remove-attendances").classList.remove("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-remove-attendances").classList.add("btn-outline-secondary")
      document.querySelector("#dashboard-volunteers-btn-add-attendances").classList.add("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-add-attendances").classList.remove("btn-outline-secondary")

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    activateRemoveAttendanceView: async function() {
      this.attendanceView = true
      this.attendanceCreateOnSelection = false

      document.querySelector("#dashboard-volunteers-btn-shifts").classList.remove("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-shifts").classList.add("btn-outline-secondary")
      document.querySelector("#dashboard-volunteers-btn-remove-attendances").classList.add("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-remove-attendances").classList.remove("btn-outline-secondary")
      document.querySelector("#dashboard-volunteers-btn-add-attendances").classList.remove("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-add-attendances").classList.add("btn-outline-secondary")

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    activateEditShiftsView: async function() {
      this.attendanceView = false

      document.querySelector("#dashboard-volunteers-btn-shifts").classList.add("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-shifts").classList.remove("btn-outline-secondary")
      document.querySelector("#dashboard-volunteers-btn-remove-attendances").classList.remove("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-remove-attendances").classList.add("btn-outline-secondary")
      document.querySelector("#dashboard-volunteers-btn-add-attendances").classList.remove("btn-secondary")
      document.querySelector("#dashboard-volunteers-btn-add-attendances").classList.add("btn-outline-secondary")

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    onDropOrResize: async function(eventResizeOrDropInfo) {
      var shift = eventResizeOrDropInfo.event;

      if (typeof shift.id == 'undefined') {
        eventResizeOrDropInfo.revert();
        return;
      }

      const response = await put(shift.extendedProps.shiftUrl, { body: JSON.stringify({
        shift: {
          starts_at: shift.start.toISOString(),
          ends_at: shift.end.toISOString(),
          volunteer_id: shift.getResources()[0].id
        }
      })})
      if (!response.ok) {
        eventResizeOrDropInfo.revert();
      }
    },
    onSelection: async function(selectionInfo) {
      if (selectionInfo.end - selectionInfo.start < 1800000) {
        // Check against a minimal duration of 30 minutes to skip random clicks directly on the calendar (e.g. to close modal dialogs)
        return
      }

      // switch functions depending on if we are currently editing shifts or attendances.
      if (this.attendanceView) {
        await this.onAttendanceSelection(selectionInfo) 
      }
      else {
        await this.onShiftSelection(selectionInfo)
      }

      this.$refs.calendar.getApi().unselect()
    },
    onShiftSelection: async function(selectionInfo) {
      const response = await get(this.shiftPositionsUrl, { query: {
        "shift[starts_at]": selectionInfo.start.toISOString(),
        "shift[ends_at]": selectionInfo.end.toISOString(),
        "shift[volunteer_id]": selectionInfo.resource.id,
      }})
      if (!response.ok) {
        return
      }

      const content = await response.text
      this.showModalDialogWithContent(content)
    },
    onAttendanceSelection: async function(selectionInfo) {
      const post_or_destroy = this.attendanceCreateOnSelection ? post : destroy
      await post_or_destroy(selectionInfo.resource.extendedProps.attendanceUrl, { query: {
        "attendance[starts_at]": selectionInfo.start.toISOString(),
        "attendance[ends_at]": selectionInfo.end.toISOString(),
      }})
    }
  }
}
</script>
<template>
  <div style="height: 0px; position: relative; top: -40px; right: 3rem;" class="text-end">
    <div class="btn-group">
      <button 
        id="dashboard-volunteers-btn-shifts"
        class="btn btn-secondary"
        @click="this.activateEditShiftsView"
        :title="this.$i18n.t('timetable.volunteers.buttons.edit_shifts.label')"
        >
        <span class="bi bi-calendar2-range me-1"></span>
        <span class="d-none d-md-inline">{{ this.$i18n.t("timetable.volunteers.buttons.edit_shifts.label") }}</span>
      </button>
      <button
        id="dashboard-volunteers-btn-add-attendances"
        class="btn btn-outline-secondary"
        @click="this.activateAddAttendanceView"
        :title="this.$i18n.t('timetable.volunteers.buttons.add_attendance.label')"
        >
        <span class="bi bi-plus-square me-1"></span>
        <span class="d-none d-md-inline">{{ this.$i18n.t("timetable.volunteers.buttons.add_attendance.label") }}</span>
      </button>
      <button
        id="dashboard-volunteers-btn-remove-attendances"
        class="btn btn-outline-secondary"
        @click="this.activateRemoveAttendanceView"
        :title="this.$i18n.t('timetable.volunteers.buttons.remove_attendance.label')"
        >
        <span class="bi bi-dash-square me-1"></span>
        <span class="d-none d-md-inline">{{ this.$i18n.t("timetable.volunteers.buttons.remove_attendance.label") }}</span>
      </button>
    </div>
  </div>

  <FullCalendar :options="calendarOptions" ref="calendar">
    <template v-slot:resourceGroupHeaderContent='arg'>
      {{ arg.fieldValue }}
    </template>

    <template v-slot:resourceCellContent='arg'>
      <span class="bi-icon bi-circle-fill me-1" :style="{color: arg.resource.extendedProps.color}"></span>
      <div style="display: inline-table">

        <template v-if="arg.resource.extendedProps.togglePresentUrl">
          <a :href="arg.resource.extendedProps.togglePresentUrl" class="btn btn-sm btn-outline-light border rounded me-1 p-0" rel="nofollow" data-remote="true" data-method="patch">
            <span v-if="arg.resource.extendedProps.isPresent" class="bi bi-person-fill-check text-success me-1" :title="this.$i18n.t('timetable.volunteers.present')"></span>
            <span v-else class="bi bi-person-fill-slash text-danger me-1" :title="this.$i18n.t('timetable.volunteers.absent')"></span>
          </a>
        </template>
        <template v-else>
          <span v-if="arg.resource.extendedProps.isPresent" class="bi bi-person-fill-check text-success me-1" :title="this.$i18n.t('timetable.volunteers.present')"></span>
          <span v-else class="bi bi-person-fill-slash text-danger me-1" :title="this.$i18n.t('timetable.volunteers.absent')"></span>
        </template>

        <a :href="arg.resource.extendedProps.url">{{ arg.resource.title }}</a>
        <div class="small">
          <div v-if="arg.resource.extendedProps.comment">{{ arg.resource.extendedProps.comment }}</div>
          <VueCustomAttributeValues :customAttributeValues="arg.resource.extendedProps.visibleCustomAttributeValues" />
        </div>
      </div>
    </template>

    <template v-slot:eventContent='arg'>
      <VueTimetableShift :event="arg.event" v-if="!this.attendanceView" />
    </template>
  </FullCalendar>
</template>
